import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, X, Expand } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { GALLERY } from "../data/gallery";

const EASE = [0.22, 1, 0.36, 1];

const Lightbox = ({ index, onClose, onStep }) => {
  const photo = index !== null ? GALLERY[index] : null;

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onStep]);

  return (
    <Dialog open={index !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gallery-lightbox" className="max-w-[96vw] w-[96vw] h-[94vh] bg-forest border-none p-0 rounded-sm overflow-hidden flex flex-col [&>button]:hidden">
        <DialogTitle className="sr-only">{photo?.title}</DialogTitle>
        {photo && (
          <>
            <div className="relative flex-1 min-h-0 flex items-center justify-center bg-black/40">
              <img data-testid="lightbox-image" src={photo.src} alt={photo.title} className="max-w-full max-h-full object-contain" />
              <button data-testid="lightbox-close-btn" onClick={onClose} className="absolute top-4 right-4 bg-forest/80 text-cream p-3 rounded-sm hover:bg-brass hover:text-forest transition-colors duration-300" aria-label="Close">
                <X size={20} />
              </button>
              <button data-testid="lightbox-prev-btn" onClick={() => onStep(-1)} className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 bg-forest/80 text-cream p-3 rounded-sm hover:bg-brass hover:text-forest transition-colors duration-300" aria-label="Previous photo">
                <ChevronLeft size={22} />
              </button>
              <button data-testid="lightbox-next-btn" onClick={() => onStep(1)} className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 bg-forest/80 text-cream p-3 rounded-sm hover:bg-brass hover:text-forest transition-colors duration-300" aria-label="Next photo">
                <ChevronRight size={22} />
              </button>
            </div>
            <div className="px-6 sm:px-10 py-5 flex flex-wrap items-center justify-between gap-3 border-t border-cream/10">
              <div>
                <p data-testid="lightbox-title" className="font-serif text-xl sm:text-2xl text-cream">{photo.title}</p>
                <p className="font-sans text-xs sm:text-sm text-cream/60 mt-1 max-w-3xl">{photo.caption}</p>
              </div>
              <p data-testid="lightbox-counter" className="font-sans text-xs tracking-[0.25em] uppercase text-brass">
                {index + 1} / {GALLERY.length}
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Tile = ({ photo, i, onOpen, className }) => (
  <motion.button
    data-testid={`gallery-photo-${photo.id}`}
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3 + i * 0.15, duration: 1, ease: EASE }}
    onClick={() => onOpen(i)}
    className={`group relative overflow-hidden bg-forest text-left ${className}`}
  >
    <img src={photo.thumb} alt={photo.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
    <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex items-end justify-between gap-4">
      <div>
        <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-brass font-semibold mb-2">Aerial · 0{i + 1}</p>
        <p className="font-serif text-xl sm:text-2xl text-cream leading-tight">{photo.title}</p>
      </div>
      <span className="shrink-0 bg-cream/10 backdrop-blur-md border border-cream/20 text-cream p-3 rounded-sm transition-colors duration-300 group-hover:bg-brass group-hover:text-forest">
        <Expand size={16} />
      </span>
    </div>
  </motion.button>
);

export default function Gallery() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Photo Gallery — Woodlake Estates Homesites | BuildInWoodlake.com";
  }, []);

  const step = (d) => setActive((i) => (i + d + GALLERY.length) % GALLERY.length);

  return (
    <div data-testid="gallery-page" className="bg-cream min-h-screen">
      <header className="fixed top-0 inset-x-0 z-50 bg-forest border-b border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link data-testid="gallery-logo" to="/" className="font-serif text-2xl tracking-tight text-cream">
            Build <span className="italic text-brass">in</span> Woodlake
          </Link>
          <Link data-testid="gallery-back-btn" to="/" className="flex items-center gap-2 font-sans text-sm text-cream/90 hover:text-brass transition-colors duration-300">
            <ArrowLeft size={16} /> Back to Site
          </Link>
        </div>
      </header>

      <section className="bg-forest pt-36 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }} className="font-sans text-xs tracking-[0.3em] uppercase text-brass font-semibold mb-6">
            Photo Gallery · Aerial Views
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 1, ease: EASE }} className="font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight max-w-3xl">
            See the Homesites <span className="italic text-brass">From Above</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: EASE }} className="mt-6 max-w-xl font-sans text-base sm:text-lg text-cream/80 leading-relaxed">
            Drone photography of the available Woodlake Estates homesites, the Verde Street cul-de-sac and the Lake Pontchartrain levee. Select any photo to view it full size.
          </motion.p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-10 lg:-mt-14 pb-24 lg:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
          <Tile photo={GALLERY[0]} i={0} onOpen={setActive} className="lg:col-span-12 aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]" />
          <Tile photo={GALLERY[1]} i={1} onOpen={setActive} className="lg:col-span-7 aspect-[4/3]" />
          <Tile photo={GALLERY[2]} i={2} onOpen={setActive} className="lg:col-span-5 aspect-[4/3] lg:aspect-auto" />
        </div>

        <div className="mt-16 lg:mt-24 border-t border-border pt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl sm:text-3xl text-forest">Ready to see it in person?</p>
            <p className="font-sans text-sm text-muted-foreground mt-2">Seven homesites are currently available. Pricing and lot details are on the main site.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="gallery-view-lots-btn" to="/#lots" className="bg-forest text-cream font-sans text-sm font-semibold tracking-wide px-7 py-3.5 rounded-sm hover:bg-[#1f3d31] transition-colors duration-300">
              View Available Lots
            </Link>
            <Link data-testid="gallery-contact-btn" to="/#contact" className="bg-brass text-forest font-sans text-sm font-semibold tracking-wide px-7 py-3.5 rounded-sm hover:bg-[#cbaa66] transition-colors duration-300">
              Contact Us Now
            </Link>
          </div>
        </div>
        <p className="mt-10 font-sans text-xs text-muted-foreground max-w-4xl leading-relaxed">
          Aerial photographs are provided for general marketing purposes. Lot boundaries are not depicted and should be independently verified with a current survey.
        </p>
      </section>

      <Lightbox index={active} onClose={() => setActive(null)} onStep={step} />
    </div>
  );
}
